import { Box, Typography, useTheme } from "@mui/material";
import { ActivityDeveloperTrack } from "@/modules/lead-page/dtos/index-v2";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { useState } from "react";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

interface DeveloperTracksListProps {
  tracks: ActivityDeveloperTrack[];
  onTrackClick?: (trackUId: string) => void;
}

const DeveloperTracksList = ({
  tracks,
  onTrackClick,
}: DeveloperTracksListProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!Array.isArray(tracks) || tracks.length === 0) {
    return null;
  }

  // Determine how many items to show based on screen size
  let maxDisplay: number;
  if (isMobile) {
    // On mobile, show up to 2 items in vertical stack
    maxDisplay = 2;
  } else {
    maxDisplay = 2
  }

  const displayTracks = isExpanded ? tracks : tracks.slice(0, maxDisplay);
  const remainingCount = tracks.length - maxDisplay;

 
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        flexWrap: "wrap",
        alignItems: "flex-start",
        gap: 2,
        marginTop: 2,
        maxWidth: "100%",
       }}
    >
      {displayTracks.map((track, index) => {
        const isClickable = !!track.trackUId && !!onTrackClick;
        return (
        <Box
          key={`${track.developerName}-${index}`}
          onClick={() => {
            if (isClickable) {
              onTrackClick(track.trackUId!);
            }
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            padding: "12px",
            borderRadius: "8px",
            backgroundColor: "rgba(0, 0, 0, 0.02)",
            border: "1px solid " + theme.nawy.colors.primitives.navy[100],
            width: isMobile ? "100%" : "fit-content",
            ...(isClickable && {
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: theme.nawy.colors.semantic.layer.secondary,
                borderColor: theme.nawy.colors.semantic.border.accent,
              },
            }),
          }}
        >
          {/* Developer Logo */}
          <Box
            sx={{
              width: "48px",
              height: "48px",
              flexShrink: 0,
              borderRadius: "8px",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
              border: "1px solid " + theme.nawy.colors.primitives.navy[100],
            }}
          >
            {track.developerLogoPath ? (
              <img
                src={track.developerLogoPath}
                alt={track.developerName || "Developer"}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
<></>
            )}
          </Box>

          {/* Developer Info */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            {/* Developer Name */}
            <Typography
              sx={{
                color: theme.nawy.colors.semantic.text.primary,
                ...theme.nawy.typography.english.variants.body.md.regular
              }}
            >
              {track.developerName || "Unknown Developer"}
            </Typography>

            {/* Developer User Name */}
            {track.developerUserName && (
              <Typography
                variant="body_sm_reg"
                sx={{
                  color: theme.nawy.colors.semantic.text.secondary,
                }}
              >
                {track.developerUserName}
              </Typography>
            )}
          </Box>
        </Box>
      )})}


      {/* Show "X more" indicator if there are more tracks than maxDisplay */}
      {remainingCount > 0 && !isExpanded && (
        <Box
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
             setIsExpanded(true);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            padding: isMobile ? 0 : "12px 20px",
            borderRadius: isMobile ? 0 : "8px",
            backgroundColor: isMobile ? "transparent" : "rgba(0, 0, 0, 0.02)",
            border: isMobile ? "none" : "1px solid " + theme.nawy.colors.primitives.navy[100],
            minWidth: "fit-content",
            whiteSpace: "nowrap",
            height: isMobile ? "auto" : "72px",
            cursor: "pointer",
            "&:hover": {
              opacity: 0.8,
            },
          }}
        >
          <Typography
                            variant="body_sm_reg"

            sx={{
              color: theme.nawy.colors.primitives.blue[500],

            }}
          >
            + {remainingCount} more
          </Typography>
          <ArrowForwardIosIcon 
            sx={{ 
              fontSize: "12px", 
              color: "#015C9A" 
            }} 
          />
        </Box>
      )}

      {/* Show "Show less" button when expanded */}
      {remainingCount > 0 && isExpanded && (
        <Box
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
             setIsExpanded(false);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            padding: isMobile ? 0 : "12px 20px",
            borderRadius: isMobile ? 0 : "8px",
            backgroundColor: isMobile ? "transparent" : "rgba(0, 0, 0, 0.02)",
            border: isMobile ? "none" : "1px solid " + theme.nawy.colors.primitives.navy[100],
            minWidth: "fit-content",
            whiteSpace: "nowrap",
            height: isMobile ? "auto" : "72px",
            cursor: "pointer",
            "&:hover": {
              opacity: 0.8,
            },
          }}
        >
          <Typography
            variant="body_sm_reg"
            sx={{
              color: theme.nawy.colors.primitives.blue[500],
            }}
          >
            Show less
          </Typography>
          <ArrowForwardIosIcon 
            sx={{ 
              fontSize: "12px", 
              color: "#015C9A",
              transform: "rotate(90deg)",
            }} 
          />
        </Box>
      )}
    </Box>
  );
};

export default DeveloperTracksList;
